import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {PortalRequest } from '@app/features/adminDashboard/interfaces/portalRequest.interface';
import { CommonDirectives } from '@app/shared/directives/common-directives';
import { AccountTypeNamePipe } from '@app/shared/pipes/accountTypeName.pipe';
import { DateFormatPipe } from '@app/shared/pipes/dateFormat.pipe';
import { UpperCasePipe } from '@app/shared/pipes/upperCase.pipe';
import { catchError, forkJoin, map, Observable, of, ReplaySubject, Subject, switchMap, takeUntil } from 'rxjs';
import { PortalRequestUserInfoModalComponent } from '../portal-request-user-info-modal/portal-request-user-info-modal.component';
import { getAccountType } from '@app/shared/services/helpers';
import { UserDetailsService } from '@app/shared/services/userDetails.service';
import { LoadSpinnerWrapperComponent } from '@app/shared/components/load-spinner-wrapper/load-spinner-wrapper.component';
import { AppState } from '@app/core/store/app.state';
import { Store } from '@ngrx/store';
import { FetchPortalRequests } from '@app/features/adminDashboard/store/admin.actions';
import { ActivatedRoute, Router } from '@angular/router';
import {PortalRequestTypePipe} from "@app/shared/pipes/portalRequestType.pipe";
import { AccountTypes } from '@app/shared/enums/accountTypes.enum';
import { ReferralUser } from '@app/core/interfaces/user.interface';

@Component({
  selector: 'app-portal-request-list',
  imports: [
    MatTableModule,
    MatSortModule,
    CommonDirectives,
    UpperCasePipe,
    DateFormatPipe,
    AccountTypeNamePipe,
    MatDialogModule,
    LoadSpinnerWrapperComponent,
    PortalRequestTypePipe
  ],
  providers: [AccountTypeNamePipe, DateFormatPipe],
  templateUrl: './portal-request-list.component.html',
  styleUrl: './portal-request-list.component.scss'
})
export class PortalRequestListComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @Input() title!: string;
  @Input() dataSource: PortalRequest[] = [];
  tableDataSource = new MatTableDataSource<PortalRequest>();
  loading = false;
  userId!: string | null;
  pendingUserId!: string | null;

  $ngDestroy = new Subject<boolean>();
  private readonly dataSourceChanges$ = new ReplaySubject<PortalRequest[]>(1);
  displayedColumns: string[] = ['accountType', 'userName', 'email', 'company', 'requestDate', 'requestType'];

  constructor(
    private dialog: MatDialog,
    private userDetailsService: UserDetailsService,
    private store: Store<AppState>,
    private accountTypeNamePipe: AccountTypeNamePipe,
    private dateFormatPipe: DateFormatPipe,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.dataSourceChanges$
      .pipe(
        switchMap(requests => this.populateReferralClinics(requests)),
        takeUntil(this.$ngDestroy)
      )
      .subscribe(requests => {
        this.tableDataSource.data = requests;
        this.tableDataSource.sort = this.sort;
      });

    this.route.params
      .pipe(takeUntil(this.$ngDestroy))
      .subscribe(params => {
        const userId = params['userId'];
        console.log('userId', userId);
        if (userId) {
          if (this.userId === userId) {
            return;
          }
          if (this.dataSource?.length > 0) {
            this.userId = userId;
            this.openUserInfoModal(this.userId);
          } else {
            this.pendingUserId = userId;
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.$ngDestroy.next(true);
    this.$ngDestroy.complete();
    this.dataSourceChanges$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource']) {
      this.dataSourceChanges$.next(this.dataSource);

      if (this.pendingUserId && this.dataSource?.length > 0) {
        this.userId = this.pendingUserId;
        this.pendingUserId = null;
        this.openUserInfoModal(this.userId);
      }
    }
  }

  private populateReferralClinics(requests: PortalRequest[]): Observable<PortalRequest[]> {
    const requestsWithClinics$ = requests.map(request => {
      if (request.accountTypeId !== AccountTypes.REFERRAL_PARTNER) {
        return of(request);
      }

      return this.userDetailsService
        .getUserDetails$(request.userIdSub, AccountTypes.REFERRAL_PARTNER)
        .pipe(
          map(userDetails => {
            const referralUser = userDetails as ReferralUser;
            const clinics = [referralUser.firstClinicId, referralUser.secondClinicId]
              .filter((clinic): clinic is string => typeof clinic === 'string' && clinic.trim().length > 0)
              .map(clinic => clinic.trim());

            return {
              ...request,
              company: clinics.join(', ')
            };
          }),
          catchError(error => {
            console.error(`Unable to load clinics for referral user ${request.userIdSub}.`, error);
            return of(request);
          })
        );
    });

    return requestsWithClinics$.length > 0 ? forkJoin(requestsWithClinics$) : of([]);
  }

  ngAfterViewInit(): void {
    this.tableDataSource.sort = this.sort;
    this.tableDataSource.sortData = (data: PortalRequest[], sort) => {
      if (!sort.active || sort.direction === '') { return data; }
      const isAsc = sort.direction === 'asc';

      return data.sort((a, b) => {
        let primaryCompare = 0;

        switch (sort.active) {
          case 'accountType':
            primaryCompare = this.accountTypeNamePipe.transform(a.accountTypeId)
              .localeCompare(this.accountTypeNamePipe.transform(b.accountTypeId));
            break;

          case 'requestDate':
            // For some reason recreating date obj is needed here, even though requestDate is typed as date.
            primaryCompare = new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime();
            break;

          case 'company':
            primaryCompare = (a.company ?? '').localeCompare(b.company ?? '');
            break;

          case 'email':
            primaryCompare = (a.email ?? '').localeCompare(b.email ?? '');
            break;

          case 'userName':
            primaryCompare = (a.userName ?? '').localeCompare(b.userName ?? '');
            break;

          default:
            const aValue = (a as any)[sort.active];
            const bValue = (b as any)[sort.active];
            if (typeof aValue === 'string' && typeof bValue === 'string') {
              primaryCompare = aValue.localeCompare(bValue);
            } else {
              primaryCompare = (aValue > bValue) ? 1 : (aValue < bValue) ? -1 : 0;
            }
        }

        // If items are not same based on primary sort then return that comparison and don't apply secondary sort
        if (primaryCompare !== 0) { return isAsc ? primaryCompare : -primaryCompare; }

        // Secondary sort logic based on primary sort column
        if (sort.active === 'userName') {
          // Secondary sort by requestDate ascending when primary is userName
          return new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime();
        } else {
          // Secondary sort by lastName ascending for other columns
          return (a.lastName ?? '').localeCompare(b.lastName ?? '');
        }
      });
    };
  }

  onRequestClick(request: PortalRequest) {
    this.router.navigate(['admin-dashboard/portal-requests', request.userIdSub], {
      replaceUrl: true,
    });
  }

  openUserInfoModal(userId: string | null) {
    if (!userId) { return; }
    if (this.loading) { return; }

    const request = this.dataSource?.find(r => r.userIdSub === userId);
    if (!request) { return ; }
    this.loading = true;
    this.userDetailsService.getUserDetails$(request.userIdSub, getAccountType(request.accountTypeId))
      .subscribe(userDetails => {
        this.loading = false;
        const dialogRef = this.dialog.open(PortalRequestUserInfoModalComponent, {
          width: '600px',
          autoFocus: false,
          disableClose: true
        });
        dialogRef.componentInstance.userAccountTypeId = request.accountTypeId;
        dialogRef.componentInstance.requestType = request.requestType;
        dialogRef.componentInstance.userDetails = userDetails;
        dialogRef.componentInstance.onUserApproved.subscribe(() => {
          this.store.dispatch(FetchPortalRequests());
        });
        dialogRef.componentInstance.onUserDenied.subscribe(() => {
          this.store.dispatch(FetchPortalRequests());
        });
        dialogRef.afterClosed().subscribe(() => {
          dialogRef.componentInstance.onUserApproved.unsubscribe();
          dialogRef.componentInstance.onUserDenied.unsubscribe();
          this.userId = null;
          this.pendingUserId = null;
          if (this.router.url.includes('admin-dashboard/portal-requests')) {
            this.router.navigate(['admin-dashboard']);
          }
          this.store.dispatch(FetchPortalRequests());
        });
      });
  }
}
